import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Menu,
  ShieldCheck,
  X,
} from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { POSTHOG_EVENTS } from "@/constants";

const megaMenuRoles = [
  {
    title: "Administrator IT",
    desc: "Infrastructura critică și obligații tehnice NIS2.",
  },
  {
    title: "CISO / Securitate",
    desc: "Strategie securitate cibernetică și evaluare risc.",
  },
  {
    title: "Responsabil conformitate",
    desc: "Aliniere cerințe legale și reglementări DNSC.",
  },
  {
    title: "Consultant extern",
    desc: "Implementare cadru NIS2 pentru clienți.",
  },
];

const megaMenuProcesses = [
  {
    title: "Evaluare risc",
    desc: "Identificare și prioritizare riscuri conform CyFUN.",
  },
  {
    title: "Notificare DNSC",
    desc: "Raportare incidente și notificare automată.",
  },
  {
    title: "Maturitate CyFUN",
    desc: "Progres implementare și planificare remediere.",
  },
  {
    title: "Guvernanță",
    desc: "Documentare decizii și trasabilitate completă.",
  },
];

const megaMenuCategories = [
  "Rol",
  "Sector",
  "Tip companie",
  "Industrii",
] as const;

const faqItems = [
  {
    q: "Ce este NIS2 și cum afectează organizația mea?",
    a: "NIS2 (Directiva UE 2022/2555) stabilește cerințe minime de securitate cibernetică pentru organizațiile din sectoare esențiale și importante. În România, a fost transpusă prin OUG 155/2024 și Legea 124/2025. Dacă activezi în energie, transport, sănătate, infrastructură digitală sau alte sectoare critice, ești obligat să te conformezi.",
  },
  {
    q: "Care sunt termenele limită pentru conformare?",
    a: "Notificarea către DNSC trebuie făcută în 30 de zile, autoevaluarea de risc în 60 de zile, iar monitorizarea și raportarea incidentelor sunt continue.",
  },
  {
    q: "Ce riscuri am dacă nu mă conformez la NIS2?",
    a: "Neconformarea poate duce la amenzi substanțiale, sancțiuni administrative și riscuri reputaționale. Autoritățile pot impune măsuri corective obligatorii.",
  },
  {
    q: "Ce este CyFUN și cum se aplică în evaluarea maturității?",
    a: "CyFUN este cadrul de referință pentru evaluarea maturității în securitate cibernetică, recomandat de DNSC pentru organizațiile din România.",
  },
  {
    q: "Cum demonstrez conformitatea către DNSC?",
    a: "Prin dosarul de audit generat de platformă, care include evidențe, justificări, versiuni și recomandări pentru raportare.",
  },
  {
    q: "Pot folosi platforma dacă sunt consultant extern?",
    a: "Da. Platforma suportă acces multi-entitate și flux dedicat pentru consultanți, cu management separat al clienților.",
  },
];

const evidenceTabs = [
  { label: "Notificare", active: true },
  { label: "Risc", active: false },
  { label: "Maturitate și remediere", active: false },
  { label: "Guvernanță", active: false },
  { label: "Detectare", active: false },
  { label: "+7", active: false },
];

const personaTabs = [
  { label: "Management", active: true },
  { label: "CISO / IT", active: false },
  { label: "Conformitate / Juridic", active: false },
  { label: "Consultant", active: false },
];

const LandingAlternativeV3 = () => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [megaMenuCat, setMegaMenuCat] = useState(0);
  const megaMenuTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openMega = () => {
    if (megaMenuTimeout.current) clearTimeout(megaMenuTimeout.current);
    setMegaMenuOpen(true);
  };
  const closeMega = () => {
    megaMenuTimeout.current = setTimeout(() => setMegaMenuOpen(false), 200);
  };

  const startEvaluation = () => {
    posthog.capture(POSTHOG_EVENTS.START_EVALUATION);
    navigate("/identificare-raportare");
  };

  const requestDemo = () => navigate("/contact");

  return (
    <div className="la25-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .la25-page * { box-sizing: border-box; margin: 0; padding: 0; }
        .la25-page { background: #F7FAFF; color: #0F172A; font-family: 'Plus Jakarta Sans', system-ui, sans-serif; }
        .la25-page a, .la25-page button { font-family: inherit; }
        .la25-wrap { width: min(1296px, calc(100% - 144px)); margin: 0 auto; }

        .la25-eyebrow { font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase; color: #2563EB; font-weight: 400; }
        .la25-h2 { font-size: clamp(32px, 4.2vw, 42px); line-height: 1.08; letter-spacing: -0.03em; font-weight: 700; color: #0F172A; }
        .la25-btn { border: none; border-radius: 999px; padding: 12px 24px; font-weight: 500; font-size: 14px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all 150ms ease; text-decoration: none; }
        .la25-btn-primary { background: #FF8400; color: #111111; }
        .la25-btn-primary:hover { background: #FF9520; }
        .la25-btn-secondary { background: #F2F3F0; color: #111111; border: 1px solid #CBCCC9; box-shadow: 0 1px 1.75px rgba(0,0,0,0.05); }
        .la25-btn-secondary:hover { background: #E7E8E5; }
        .la25-tab { border: 1px solid #CBD5E1; background: #FFFFFF; padding: 8px 14px; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: #334155; cursor: pointer; transition: all 150ms ease; }
        .la25-tab.active { background: #111827; border-color: #111827; color: #FFFFFF; }

        /* Top Banner */
        .la25-banner { position: sticky; top: 0; z-index: 100; height: 44px; display: flex; align-items: center; justify-content: space-between; padding: 0 20px; border: 1px solid #1E293B; background: linear-gradient(90deg, #0B1220 0%, #111827 50%, #0B1220 100%); }
        .la25-banner-msg { font-size: 11px; color: #CBD5E1; letter-spacing: 0.5px; }
        .la25-banner-cta { display: flex; align-items: center; gap: 8px; padding: 7px 10px; background: #1E293B; border: 1px solid #334155; font-size: 10px; color: #F8FAFC; letter-spacing: 0.5px; cursor: pointer; transition: all 150ms ease; }
        .la25-banner-cta:hover { background: #334155; }
        .la25-banner-cta .arrow { color: #93C5FD; }

        /* Hero */
        .la25-hero { background: linear-gradient(145deg, #0B1220 0%, #0E1A34 45%, #123A7A 100%); }
        .la25-hero-inner { padding: 24px 0 88px; }
        .la25-nav-wrap { position: relative; margin-bottom: 24px; }
        .la25-nav { display: flex; align-items: center; justify-content: space-between; }
        .la25-logo { display: flex; align-items: center; gap: 10px; }
        .la25-logo-mark { width: 14px; height: 14px; background: #2563EB; border-radius: 2px; }
        .la25-logo-text { font-size: 18px; font-weight: 700; color: #FFFFFF; }
        .la25-nav-links { display: flex; align-items: center; gap: 24px; }
        .la25-nav-link { font-size: 12px; letter-spacing: 1px; color: #CBD5E1; cursor: pointer; transition: color 150ms; position: relative; padding: 8px 0; }
        .la25-nav-link:hover { color: #FFFFFF; }
        .la25-menu-btn { display: none; border: 1px solid #334155; background: transparent; color: #E2E8F0; border-radius: 10px; width: 42px; height: 42px; align-items: center; justify-content: center; cursor: pointer; }
        .la25-mobile-menu { display: none; border: 1px solid #334155; background: #0B1220; border-radius: 14px; padding: 16px; margin-bottom: 24px; }
        .la25-mobile-menu .la25-nav-links { flex-direction: column; align-items: flex-start; gap: 12px; margin-bottom: 12px; }

        /* Mega Menu */
        .la25-mega { position: absolute; top: 100%; left: 50%; transform: translateX(-50%); width: 864px; background: #FFFFFF; border-radius: 0 0 8px 8px; padding: 32px; display: flex; gap: 0; box-shadow: 0 8px 24px rgba(0,0,0,0.08); z-index: 50; margin-top: 4px; }
        .la25-mega-cats { display: flex; flex-direction: column; gap: 2px; width: 155px; padding-right: 20px; flex-shrink: 0; }
        .la25-mega-cat { padding: 12px 16px; border-radius: 6px; font-size: 16px; color: #64748B; cursor: pointer; transition: all 120ms; }
        .la25-mega-cat:hover { background: #F8FAFC; }
        .la25-mega-cat.active { background: #F1F5F9; color: #0F172A; font-weight: 600; display: flex; justify-content: space-between; align-items: center; }
        .la25-mega-cat.active::after { content: '>'; color: #2563EB; font-weight: 600; }
        .la25-mega-divider { width: 1px; background: #E2E8F0; align-self: stretch; flex-shrink: 0; }
        .la25-mega-main { flex: 1; display: flex; gap: 16px; min-width: 0; }
        .la25-mega-col { flex: 1; display: flex; flex-direction: column; gap: 4px; padding: 0 16px; }
        .la25-mega-col-title { font-size: 12px; letter-spacing: 1.2px; color: #94A3B8; margin-bottom: 4px; }
        .la25-mega-item { padding: 8px 10px; border-radius: 6px; cursor: pointer; transition: background 120ms; }
        .la25-mega-item:hover { background: #F8FAFC; }
        .la25-mega-item-title { font-size: 16px; font-weight: 600; color: #0F172A; }
        .la25-mega-item-desc { font-size: 13px; color: #64748B; margin-top: 2px; }
        .la25-mega-featured { width: 210px; flex-shrink: 0; background: #F1F5F9; border-radius: 8px; border: 1px solid #E2E8F0; padding: 20px; display: flex; flex-direction: column; gap: 12px; }
        .la25-mega-featured-visual { background: linear-gradient(135deg, #2563EB, #1E40AF); border-radius: 6px; height: 100px; display: flex; align-items: center; justify-content: center; color: #FFFFFF; }
        .la25-mega-featured-title { font-size: 16px; font-weight: 700; color: #0F172A; }
        .la25-mega-featured-desc { font-size: 13px; line-height: 1.4; color: #64748B; }
        .la25-mega-featured-link { font-size: 13px; font-weight: 600; color: #2563EB; cursor: pointer; }

        .la25-hero-body { display: flex; align-items: center; gap: 48px; }
        .la25-hero-left { flex: 1; display: flex; flex-direction: column; gap: 20px; }
        .la25-hero-title { font-size: clamp(40px, 5.8vw, 58px); line-height: 1.03; letter-spacing: -0.038em; font-weight: 800; color: #FFFFFF; max-width: 640px; }
        .la25-hero-sub { font-size: 16px; line-height: 1.45; color: #C7D2FE; max-width: 640px; }
        .la25-hero-ctas { display: flex; flex-wrap: wrap; gap: 12px; }
        .la25-hero-proof { font-size: 12px; letter-spacing: 0.8px; color: #93C5FD; }
        .la25-hero-visual { width: 520px; flex-shrink: 0; background: #0B1220; border: 1px solid rgba(96,165,250,0.4); padding: 14px; display: flex; flex-direction: column; gap: 12px; }
        .la25-hero-visual-head { display: flex; justify-content: space-between; align-items: center; }
        .la25-hero-visual-head span { font-size: 10px; letter-spacing: 0.9px; }
        .la25-hero-visual-head .left { color: #DBEAFE; }
        .la25-hero-visual-head .right { color: #93C5FD; }
        .la25-hero-slide { background: #0F172A; border: 1px solid #1E3A8A; padding: 14px; display: flex; flex-direction: column; gap: 8px; }
        .la25-hero-slide-tag { font-size: 10px; letter-spacing: 1.1px; color: #60A5FA; }
        .la25-hero-slide-title { font-size: 18px; font-weight: 700; line-height: 1.2; color: #F8FAFC; }
        .la25-hero-slide-desc { font-size: 11px; line-height: 1.4; color: #BFDBFE; }
        .la25-hero-slide-meta { font-size: 10px; letter-spacing: 0.6px; color: #93C5FD; }
        .la25-hero-controls { display: flex; justify-content: space-between; align-items: center; }
        .la25-hero-controls span { font-size: 10px; letter-spacing: 0.8px; color: #93C5FD; cursor: pointer; }
        .la25-hero-dots { display: flex; gap: 6px; align-items: center; }
        .la25-hero-dot { width: 6px; height: 6px; border-radius: 50%; background: #334155; }
        .la25-hero-dot.active { background: #60A5FA; }

        /* Trust Section */
        .la25-trust { background: #FFFFFF; padding: 80px 0; }
        .la25-trust-title { font-size: 38px; font-weight: 700; letter-spacing: -1.2px; line-height: 1.1; color: #0F172A; margin-top: 16px; }
        .la25-trust-body { display: flex; gap: 24px; margin-top: 22px; }
        .la25-trust-main { flex: 1; display: flex; flex-direction: column; gap: 12px; }
        .la25-trust-logos-label { font-size: 11px; letter-spacing: 1px; color: #64748B; }
        .la25-trust-logos { display: flex; flex-wrap: wrap; gap: 10px; }
        .la25-trust-chip { background: #F8FAFC; border: 1px solid #E2E8F0; padding: 10px 14px; font-size: 12px; letter-spacing: 1.3px; color: #0F172A; }
        .la25-trust-stats { display: flex; gap: 24px; flex-wrap: wrap; margin-top: 4px; }
        .la25-trust-stat-val { font-size: 30px; font-weight: 800; letter-spacing: -1px; color: #111827; }
        .la25-trust-stat-label { font-size: 10px; letter-spacing: 0.8px; color: #64748B; margin-top: 2px; }
        .la25-trust-quote { width: 420px; flex-shrink: 0; background: #0F172A; border: 1px solid #2E4A86; padding: 18px 20px; display: flex; flex-direction: column; gap: 10px; }
        .la25-trust-quote-text { font-size: 18px; line-height: 1.25; color: #F8FAFC; }
        .la25-trust-quote-meta { font-size: 11px; letter-spacing: 0.8px; color: #94A3B8; }

        /* Operating Flow */
        .la25-flow { background: #F8FAFC; padding: 72px 0; }
        .la25-flow .la25-wrap { }
        .la25-flow-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; margin-top: 24px; }
        .la25-flow-card { background: #FFFFFF; border: 1px solid #E2E8F0; padding: 18px; display: flex; flex-direction: column; gap: 12px; }
        .la25-flow-card-num { font-size: 11px; letter-spacing: 1.5px; color: #1D4ED8; }
        .la25-flow-card-title { font-size: 24px; font-weight: 700; letter-spacing: -0.6px; color: #0F172A; }
        .la25-flow-card-desc { font-size: 13px; line-height: 1.45; color: #475569; }
        .la25-flow-cta { margin-top: 18px; }

        /* Separator */
        .la25-separator { background: #020617; padding: 28px 0; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 403px; text-align: center; position: relative; overflow: hidden; }
        .la25-separator::before { content: ''; position: absolute; inset: 0; background: linear-gradient(180deg, #020617 0%, rgba(0,0,0,0.56) 50%, #020617 100%); z-index: 1; }
        .la25-separator > * { position: relative; z-index: 2; }
        .la25-separator-title { font-size: clamp(36px, 5vw, 52px); font-weight: 800; letter-spacing: -1.6px; line-height: 1.06; color: #FFFFFF; max-width: 980px; }
        .la25-separator-sub { font-size: 15px; line-height: 1.45; color: #E2E8F0; max-width: 760px; margin-top: 14px; }
        .la25-separator-cta { margin-top: 14px; }

        /* Product Evidence */
        .la25-evidence { background: #F8FAFC; padding: 96px 0; }
        .la25-evidence-tabs { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 20px; }
        .la25-evidence-body { display: flex; gap: 20px; margin-top: 20px; }
        .la25-evidence-main { flex: 1; display: flex; flex-direction: column; gap: 10px; }
        .la25-evidence-main-title { font-size: 24px; font-weight: 700; letter-spacing: -0.6px; }
        .la25-evidence-shot { border: 1px solid #334155; background: #0F172A; height: 320px; display: flex; align-items: center; justify-content: center; color: #93C5FD; font-size: 13px; letter-spacing: 0.5px; }
        .la25-evidence-side { width: 400px; flex-shrink: 0; display: flex; flex-direction: column; gap: 12px; }
        .la25-evidence-video-title { font-size: 24px; font-weight: 700; letter-spacing: -0.6px; }
        .la25-evidence-video-card { background: #FFFFFF; border: 1px solid #CBD5E1; display: flex; flex-direction: column; gap: 10px; }
        .la25-evidence-video-shot { border: 1px solid #334155; background: #111827; height: 190px; display: flex; align-items: center; justify-content: center; color: #94A3B8; font-size: 13px; }
        .la25-evidence-video-note { font-size: 12px; line-height: 1.45; color: #475569; padding: 0 14px 14px; }

        /* Capabilities */
        .la25-capabilities { background: #FFFFFF; padding: 96px 0; }
        .la25-cap-tabs { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 20px; }
        .la25-cap-body { display: flex; gap: 20px; margin-top: 20px; }
        .la25-cap-list { flex: 1; display: flex; flex-direction: column; gap: 12px; }
        .la25-cap-card { background: #F8FAFC; border: 1px solid #E2E8F0; padding: 14px; display: flex; flex-direction: column; gap: 8px; }
        .la25-cap-card-title { font-size: 22px; font-weight: 700; letter-spacing: -0.5px; color: #0F172A; }
        .la25-cap-card-desc { font-size: 13px; line-height: 1.45; color: #475569; }
        .la25-persona-card { width: 420px; flex-shrink: 0; background: #0F172A; border: 1px solid #1E293B; padding: 20px; display: flex; flex-direction: column; gap: 14px; }
        .la25-persona-title { font-size: 28px; font-weight: 700; letter-spacing: -0.8px; color: #FFFFFF; }
        .la25-persona-bullet { font-size: 13px; line-height: 1.45; color: #C7D2FE; }

        /* Legal Framework */
        .la25-legal { background: #F8FBFF; padding: 96px 0; }
        .la25-legal-title { font-size: 46px; font-weight: 800; letter-spacing: -1.5px; line-height: 1.06; color: #0F172A; margin-top: 16px; }
        .la25-legal-sub { font-size: 14px; line-height: 1.5; color: #475569; margin-top: 12px; }
        .la25-legal-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; margin-top: 24px; }
        .la25-legal-card { background: #FFFFFF; border: 1px solid #D6E6FF; padding: 24px 22px; display: flex; flex-direction: column; gap: 10px; }
        .la25-legal-card-tag { font-size: 11px; letter-spacing: 1.2px; }
        .la25-legal-card-title { font-size: 24px; font-weight: 700; letter-spacing: -0.6px; color: #0F172A; }
        .la25-legal-card-sub { font-size: 12px; line-height: 1.4; }
        .la25-legal-card-desc { font-size: 12px; line-height: 1.5; color: #475569; }
        .la25-legal-warn { background: #EAF2FF; border: 1px solid #BFD4FF; padding: 20px 22px; display: flex; flex-direction: column; gap: 8px; margin-top: 18px; }
        .la25-legal-warn-title { font-size: 22px; font-weight: 700; letter-spacing: -0.5px; color: #1D4ED8; }
        .la25-legal-warn-desc { font-size: 13px; line-height: 1.55; color: #334155; }

        /* FAQ */
        .la25-faq { background: #FFFFFF; padding: 96px 0; }
        .la25-faq-list { margin-top: 24px; }
        .la25-faq-item { border-bottom: 1px solid #CBCCC9; }
        .la25-faq-trigger { display: flex; justify-content: space-between; align-items: center; gap: 18px; padding: 16px 0; cursor: pointer; width: 100%; background: none; border: none; text-align: left; font-family: inherit; }
        .la25-faq-trigger-text { font-size: 16px; font-weight: 500; line-height: 1.5; color: #111111; }
        .la25-faq-answer { padding: 0 0 16px; font-size: 14px; line-height: 1.43; color: #666666; }

        /* Final CTA */
        .la25-final-cta { background: #0F2E6D; padding: 96px 0; }
        .la25-final-title { font-size: clamp(32px, 4.2vw, 42px); font-weight: 800; letter-spacing: -1.8px; line-height: 1.08; color: #FFFFFF; max-width: 980px; }
        .la25-final-desc { font-size: 14px; line-height: 1.45; color: #DBEAFE; margin-top: 12px; }
        .la25-final-btns { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 20px; }

        /* Footer */
        .la25-footer { background: #0B2452; padding: 48px 0; }
        .la25-footer-top { display: flex; justify-content: space-between; align-items: start; gap: 24px; flex-wrap: wrap; }
        .la25-footer-brand { font-size: 24px; font-weight: 700; color: #FFFFFF; }
        .la25-footer-tagline { font-size: 12px; letter-spacing: 1px; color: #94A3B8; margin-top: 6px; }
        .la25-footer-nav { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
        .la25-footer-link { font-size: 12px; letter-spacing: 1px; color: #E2E8F0; cursor: pointer; background: none; border: none; font-family: inherit; transition: color 150ms; }
        .la25-footer-link:hover { color: #FFFFFF; }
        .la25-footer-line { height: 1px; background: #1E293B; margin: 24px 0 14px; border: none; }
        .la25-footer-copy { font-size: 11px; letter-spacing: 0.8px; color: #64748B; }

        /* Mobile CTA */
        .la25-mobile-cta { display: none; position: sticky; bottom: 0; z-index: 80; background: rgba(11,18,32,0.94); backdrop-filter: blur(10px); border-top: 1px solid #334155; padding: 10px 12px; gap: 10px; }
        .la25-mobile-cta button { flex: 1; justify-content: center; }

        /* Responsive */
        @media (max-width: 1080px) {
          .la25-hero-body, .la25-trust-body, .la25-cap-body, .la25-evidence-body { flex-direction: column; }
          .la25-hero-visual, .la25-trust-quote, .la25-persona-card, .la25-evidence-side { width: 100%; }
          .la25-legal-cards, .la25-flow-cards { grid-template-columns: 1fr; }
        }
        @media (max-width: 920px) {
          .la25-wrap { width: min(1296px, calc(100% - 32px)); }
          .la25-nav-links, .la25-nav .la25-btn-secondary, .la25-mega { display: none !important; }
          .la25-menu-btn { display: inline-flex; }
          .la25-mobile-menu { display: block; }
          .la25-mobile-cta { display: flex; }
          .la25-banner-msg { font-size: 10px; }
        }
      `}</style>

      {/* Top Banner */}
      <div className="la25-banner">
        <span className="la25-banner-msg">
          Termen de conformare NIS2: 30 de zile de la notificarea DNSC
        </span>
        <button
          type="button"
          className="la25-banner-cta"
          onClick={startEvaluation}
        >
          <span>Verifică acum</span>
          <span className="arrow">&gt;</span>
        </button>
      </div>

      {/* Hero */}
      <section className="la25-hero">
        <div className="la25-wrap la25-hero-inner">
          <div className="la25-nav-wrap" onMouseLeave={closeMega}>
            <nav className="la25-nav">
              <div className="la25-logo">
                <span className="la25-logo-mark" />
                <span className="la25-logo-text">SECURENIS</span>
              </div>
              <div className="la25-nav-links">
                <span className="la25-nav-link" onMouseEnter={openMega}>
                  Platformă
                </span>
                <span className="la25-nav-link" onMouseEnter={openMega}>
                  Soluții
                </span>
                <span className="la25-nav-link" onMouseEnter={closeMega}>
                  Resurse
                </span>
              </div>
              <button
                type="button"
                className="la25-btn la25-btn-secondary"
                onClick={requestDemo}
              >
                Solicita demo
              </button>
              <button
                type="button"
                className="la25-menu-btn"
                onClick={() => setMobileMenuOpen((v) => !v)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            </nav>

            {megaMenuOpen && (
              <div
                className="la25-mega"
                onMouseEnter={openMega}
                onMouseLeave={closeMega}
              >
                <div className="la25-mega-cats">
                  {megaMenuCategories.map((cat, i) => (
                    <div
                      key={cat}
                      className={`la25-mega-cat${megaMenuCat === i ? " active" : ""}`}
                      onMouseEnter={() => setMegaMenuCat(i)}
                    >
                      {cat}
                    </div>
                  ))}
                </div>
                <div className="la25-mega-divider" />
                <div className="la25-mega-main">
                  <div className="la25-mega-col">
                    <span className="la25-mega-col-title">DUPĂ ROL</span>
                    {megaMenuRoles.map((item) => (
                      <div
                        key={item.title}
                        className="la25-mega-item"
                        onClick={startEvaluation}
                      >
                        <div className="la25-mega-item-title">{item.title}</div>
                        <div className="la25-mega-item-desc">{item.desc}</div>
                      </div>
                    ))}
                  </div>
                  <div className="la25-mega-col">
                    <span className="la25-mega-col-title">DUPĂ PROCES</span>
                    {megaMenuProcesses.map((item) => (
                      <div
                        key={item.title}
                        className="la25-mega-item"
                        onClick={startEvaluation}
                      >
                        <div className="la25-mega-item-title">{item.title}</div>
                        <div className="la25-mega-item-desc">{item.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="la25-mega-divider" />
                <div className="la25-mega-featured">
                  <div className="la25-mega-featured-visual">
                    <ShieldCheck size={32} />
                  </div>
                  <div className="la25-mega-featured-title">
                    SECURENIS Platform
                  </div>
                  <div className="la25-mega-featured-desc">
                    Platforma completă pentru conformitate NIS2. Evaluare,
                    notificare și monitorizare într-un singur loc.
                  </div>
                  <span
                    className="la25-mega-featured-link"
                    onClick={startEvaluation}
                  >
                    Află mai multe →
                  </span>
                </div>
              </div>
            )}
          </div>

          {mobileMenuOpen && (
            <div className="la25-mobile-menu">
              <div className="la25-nav-links">
                <span className="la25-nav-link">Platformă</span>
                <span className="la25-nav-link">Soluții</span>
                <span className="la25-nav-link">Resurse</span>
              </div>
              <button
                type="button"
                className="la25-btn la25-btn-secondary"
                onClick={requestDemo}
              >
                Solicita demo
              </button>
            </div>
          )}

          <div className="la25-hero-body">
            <div className="la25-hero-left">
              <h1 className="la25-hero-title">
                Conformitate NIS2, cu impact real în primele 30 de zile.
              </h1>
              <p className="la25-hero-sub">
                Automatizezi notificarea, maparea cerințelor și evidența pentru
                DNSC într-un flux unificat.
              </p>
              <div className="la25-hero-ctas">
                <button
                  type="button"
                  className="la25-btn la25-btn-primary"
                  onClick={startEvaluation}
                >
                  Începe evaluarea <ArrowRight size={16} />
                </button>
                <button
                  type="button"
                  className="la25-btn la25-btn-secondary"
                  onClick={requestDemo}
                >
                  Solicita demo
                </button>
              </div>
              <p className="la25-hero-proof">
                Folosit de companii pentru progres complet NIS2.
              </p>
            </div>

            <div className="la25-hero-visual">
              <div className="la25-hero-visual-head">
                <span className="left">Noutăți DNSC și calendar NIS2</span>
                <span className="right">Ecran 1/3</span>
              </div>
              <div className="la25-hero-slide">
                <span className="la25-hero-slide-tag">
                  ACTUALIZARE REGLEMENTARE
                </span>
                <p className="la25-hero-slide-title">
                  Ordinele DNSC 1/2025 și 2/2025 operaționalizează notificarea
                  și evaluarea riscului.
                </p>
                <p className="la25-hero-slide-desc">
                  Context timeline: Directiva UE 2022/2555 (decembrie 2022)
                  -&gt; termen transpunere 17 octombrie 2024 -&gt; cadrul RO
                  prin OUG 155/2024 și Legea 124/2025.
                </p>
                <span className="la25-hero-slide-meta">
                  Sursa: flux public DNSC / legislatie NIS2 (actualizare
                  periodica)
                </span>
              </div>
              <div className="la25-hero-controls">
                <span>&lt; Prev</span>
                <div className="la25-hero-dots">
                  <span className="la25-hero-dot active" />
                  <span className="la25-hero-dot" />
                  <span className="la25-hero-dot" />
                </div>
                <span>Next &gt;</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="la25-trust">
        <div className="la25-wrap">
          <p className="la25-eyebrow">ÎNCREDERE ANALIȘTI ȘI CLIENȚI</p>
          <h2 className="la25-trust-title">
            Un model de platformă conectată pentru echipele de IT, legal, risc
            și conformitate.
          </h2>
          <div className="la25-trust-body">
            <div className="la25-trust-main">
              <div>
                <p className="la25-trust-logos-label">&nbsp;</p>
                <div className="la25-trust-logos">
                  <span className="la25-trust-chip">Anexa 1</span>
                  <span className="la25-trust-chip">Anexa 2</span>
                  <span className="la25-trust-chip">CyFUN</span>
                  <span className="la25-trust-chip">NIST-CSF</span>
                </div>
              </div>
              <div className="la25-trust-stats">
                <div>
                  <p className="la25-trust-stat-val">217+</p>
                  <p className="la25-trust-stat-label">
                    controale în biblioteca unificată
                  </p>
                </div>
                <div>
                  <p className="la25-trust-stat-val">4x</p>
                  <p className="la25-trust-stat-label">
                    de la luni de implementare, la saptamani sau chiar zile
                  </p>
                </div>
              </div>
            </div>
            <aside className="la25-trust-quote">
              <p className="la25-trust-quote-text">
                &ldquo;O singură platformă pentru execuția riscului,
                conformității, securității cibernetice și auditului.&rdquo;
              </p>
              <span className="la25-trust-quote-meta">
                Șef Guvernanță · Companie reglementată
              </span>
            </aside>
          </div>
        </div>
      </section>

      {/* Operating Flow */}
      <section className="la25-flow">
        <div className="la25-wrap">
          <p className="la25-eyebrow">FLUX OPERAȚIONAL</p>
          <h2 className="la25-h2">
            Un singur flux pentru administrator, CISO și juridic.
          </h2>
          <div className="la25-flow-cards">
            <article className="la25-flow-card">
              <span className="la25-flow-card-num">01</span>
              <h3 className="la25-flow-card-title">Obligativitate entitati</h3>
              <p className="la25-flow-card-desc">
                Pornești cu CUI. Platforma identifică sectorul, criticitatea și
                ipoteza de încadrare.
              </p>
            </article>
            <article className="la25-flow-card">
              <span className="la25-flow-card-num">02</span>
              <h3 className="la25-flow-card-title">
                Implementare NIS2 (CyFUN)
              </h3>
              <p className="la25-flow-card-desc">
                Rulezi analize impact, maturitate si planuri de remediere cu
                evidenta justificativa.
              </p>
            </article>
            <article className="la25-flow-card">
              <span className="la25-flow-card-num">03</span>
              <h3 className="la25-flow-card-title">
                Îmbunătățire continuă și auditare
              </h3>
              <p className="la25-flow-card-desc">
                Generezi dosar cu decizii, versiuni si recomandari pentru
                raportare DNSC si procurement.
              </p>
            </article>
          </div>
          <div className="la25-flow-cta">
            <button
              type="button"
              className="la25-btn la25-btn-secondary"
              onClick={requestDemo}
            >
              Vezi demo ghidat
            </button>
          </div>
        </div>
      </section>

      {/* Separator CTA */}
      <section className="la25-separator">
        <h2 className="la25-separator-title">
          SECURENIS automatizează conformitatea, economisind sute de ore.
        </h2>
        <p className="la25-separator-sub">
          Fii cu un pas înaintea cerințelor DNSC și evită riscul de amenzi.
        </p>
        <div className="la25-separator-cta">
          <button
            type="button"
            className="la25-btn la25-btn-primary"
            onClick={startEvaluation}
          >
            Vezi platforma
          </button>
        </div>
      </section>

      {/* Product Evidence */}
      <section className="la25-evidence">
        <div className="la25-wrap">
          <p className="la25-eyebrow">DOVEZI PRODUS</p>
          <h2 className="la25-h2">
            Dovezi vizuale: clasificare, risc, maturitate.
          </h2>
          <div className="la25-evidence-tabs">
            {evidenceTabs.map((tab) => (
              <span
                key={tab.label}
                className={`la25-tab${tab.active ? " active" : ""}`}
              >
                {tab.label}
              </span>
            ))}
          </div>
          <div className="la25-evidence-body">
            <div className="la25-evidence-main">
              <h3 className="la25-evidence-main-title">
                Rezultat clasificare + context regulator
              </h3>
              <div className="la25-evidence-shot">
                Screenshot placeholder 16:9
              </div>
            </div>
            <aside className="la25-evidence-side">
              <h3 className="la25-evidence-video-title">
                Demonstrație 60-90 sec
              </h3>
              <div className="la25-evidence-video-card">
                <div className="la25-evidence-video-shot">
                  Video placeholder
                </div>
                <p className="la25-evidence-video-note">
                  Include scenariu complet: input CUI → analiză risc → evidență
                  exportabilă.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="la25-capabilities">
        <div className="la25-wrap">
          <p className="la25-eyebrow">CAPABILITATI PLATFORMA</p>
          <h2 className="la25-h2">Claritate pentru toate rolurile implicate</h2>
          <div className="la25-cap-tabs">
            {personaTabs.map((tab) => (
              <span
                key={tab.label}
                className={`la25-tab${tab.active ? " active" : ""}`}
              >
                {tab.label}
              </span>
            ))}
          </div>
          <div className="la25-cap-body">
            <div className="la25-cap-list">
              <article className="la25-cap-card">
                <h3 className="la25-cap-card-title">
                  Clasificare sectoriala, planificare, rezultate
                </h3>
                <p className="la25-cap-card-desc">
                  Reguli explicite de incadrare cu justificari salvate la nivel
                  de decizie.
                </p>
              </article>
              <article className="la25-cap-card">
                <h3 className="la25-cap-card-title">
                  Risc, maturitate, remediere
                </h3>
                <p className="la25-cap-card-desc">
                  Prioritizare orientata business cu planuri actionabile si
                  responsabilitati clare.
                </p>
              </article>
              <article className="la25-cap-card">
                <h3 className="la25-cap-card-title">Auditare, raportare</h3>
                <p className="la25-cap-card-desc">
                  Versionare evidenta, log de decizie si livrabile pentru audit
                  intern si extern.
                </p>
              </article>
            </div>
            <aside className="la25-persona-card">
              <h3 className="la25-persona-title">Pentru management</h3>
              <p className="la25-persona-bullet">
                • Vizibilitate asupra riscurilor critice pe entități
              </p>
              <p className="la25-persona-bullet">
                • Justificări ușor de prezentat în board și audit
              </p>
              <p className="la25-persona-bullet">
                • Roadmap trimestrial cu progres măsurabil
              </p>
            </aside>
          </div>
        </div>
      </section>

      {/* Legal Framework */}
      <section className="la25-legal">
        <div className="la25-wrap">
          <p className="la25-eyebrow">CADRU REGLEMENTAR</p>
          <h2 className="la25-legal-title">Baza legală NIS2 în România</h2>
          <p className="la25-legal-sub">
            Construit pe Directiva (UE) 2022/2555, OUG 155/2024 și Legea
            124/2025 pentru programe de guvernanță enterprise.
          </p>
          <div className="la25-legal-cards">
            <article className="la25-legal-card">
              <span
                className="la25-legal-card-tag"
                style={{ color: "#60A5FA" }}
              >
                DIRECTIVA UE
              </span>
              <h3 className="la25-legal-card-title">
                Directiva (UE) 2022/2555
              </h3>
              <span
                className="la25-legal-card-sub"
                style={{ color: "#60A5FA" }}
              >
                NIS2 - cadrul european pentru securitate cibernetică
              </span>
              <p className="la25-legal-card-desc">
                Standarde minime de securitate pentru organizatiile din sectoare
                critice.
              </p>
            </article>
            <article className="la25-legal-card">
              <span
                className="la25-legal-card-tag"
                style={{ color: "#F59E0B" }}
              >
                TRANSPUNERE
              </span>
              <h3 className="la25-legal-card-title">OUG nr. 155/2024</h3>
              <span
                className="la25-legal-card-sub"
                style={{ color: "#F59E0B" }}
              >
                Transpunere națională
              </span>
              <p className="la25-legal-card-desc">
                Stabileste cadrul legal national pentru identificare, evaluare
                risc si obligatii de raportare.
              </p>
            </article>
            <article className="la25-legal-card">
              <span
                className="la25-legal-card-tag"
                style={{ color: "#10B981" }}
              >
                LEGE
              </span>
              <h3 className="la25-legal-card-title">Legea nr. 124/2025</h3>
              <span
                className="la25-legal-card-sub"
                style={{ color: "#10B981" }}
              >
                Aprobare si completare
              </span>
              <p className="la25-legal-card-desc">
                Finalizeaza transpunerea NIS2 in legislatia nationala si
                clarifica cerintele operationale.
              </p>
            </article>
          </div>
          <div className="la25-legal-warn">
            <h3 className="la25-legal-warn-title">
              Obligație de conformitate continuă
            </h3>
            <p className="la25-legal-warn-desc">
              Conformitatea este continuă: notificare în 30 de zile,
              autoevaluare de risc în 60 de zile, apoi monitorizare susținută și
              raportarea incidentelor.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="la25-faq">
        <div className="la25-wrap">
          <p className="la25-eyebrow">ÎNTREBĂRI FRECVENTE</p>
          <h2 className="la25-h2">
            Răspunsuri la întrebările cheie despre NIS2
          </h2>
          <div className="la25-faq-list">
            {faqItems.map((faq, i) => (
              <div key={faq.q} className="la25-faq-item">
                <button
                  type="button"
                  className="la25-faq-trigger"
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                >
                  <span className="la25-faq-trigger-text">{faq.q}</span>
                  {openFaq === i ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </button>
                {openFaq === i && <p className="la25-faq-answer">{faq.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="la25-final-cta">
        <div className="la25-wrap">
          <p className="la25-eyebrow" style={{ color: "#93C5FD" }}>
            ÎNCEPE ACUM
          </p>
          <h2 className="la25-final-title">
            Vezi cum o abordare holistică accelerează pregătirea pentru NIS2.
          </h2>
          <p className="la25-final-desc">
            Solicită un demo pentru a mapa starea curentă și a prioritiza o
            foaie de parcurs măsurabilă pentru companii.
          </p>
          <div className="la25-final-btns">
            <button
              type="button"
              className="la25-btn la25-btn-primary"
              onClick={startEvaluation}
            >
              Începe evaluarea
            </button>
            <button
              type="button"
              className="la25-btn la25-btn-secondary"
              onClick={requestDemo}
            >
              Vorbeste cu un consultant
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="la25-footer">
        <div className="la25-wrap">
          <div className="la25-footer-top">
            <div>
              <h3 className="la25-footer-brand">SECURENIS pentru companii</h3>
              <p className="la25-footer-tagline">
                Claritate de guvernanță pentru decizii NIS2.
              </p>
            </div>
            <nav className="la25-footer-nav">
              <span className="la25-footer-link">Centru de Încredere</span>
              <span className="la25-footer-link">Pachet de Securitate</span>
              <button
                type="button"
                className="la25-footer-link"
                onClick={requestDemo}
              >
                Contact pentru companii
              </button>
            </nav>
          </div>
          <hr className="la25-footer-line" />
          <p className="la25-footer-copy">
            © 2026 SECURENIS · Toate drepturile rezervate · Fără eticheta Alpha
            pe această variantă.
          </p>
        </div>
      </footer>

      {/* Mobile sticky CTA */}
      <div className="la25-mobile-cta">
        <button
          type="button"
          className="la25-btn la25-btn-primary"
          onClick={startEvaluation}
        >
          Începe evaluarea
        </button>
        <button
          type="button"
          className="la25-btn la25-btn-secondary"
          onClick={requestDemo}
        >
          Solicita demo
        </button>
      </div>
    </div>
  );
};

export default LandingAlternativeV3;
